import React, { useState, useEffect, useCallback } from 'react';

import { useGalleryLightbox } from './GalleryLightbox';

/* Shared 3D Coverflow carousel. The centre photo takes the stage while its
   neighbours angle away in 3D. Arrow keys / buttons / dots glide the carousel;
   clicking the centre photo opens it full-screen. Pass a `photos` array of
   { src, cap } — every gallery page that uses the coverflow design shares this
   one implementation. */
const GalleryCoverflowCarousel = ({ photos }) => {
    const lb = useGalleryLightbox(photos);
    const [active, setActive] = useState(0);

    const go = useCallback((dir) => {
        setActive((a) => (a + dir + photos.length) % photos.length);
    }, [photos.length]);

    // arrow keys move the carousel — but only while the lightbox is closed
    useEffect(() => {
        if (lb.index >= 0) return undefined;
        const onKey = (e) => {
            if (e.key === 'ArrowLeft') go(-1);
            else if (e.key === 'ArrowRight') go(1);
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [go, lb.index]);

    const slideStyle = (i) => {
        const n = photos.length;
        let off = i - active;
        // Wrap to the shortest signed distance so the carousel loops seamlessly:
        // past the last photo the first ones curve back in from the other side
        // instead of the whole strip snapping back to the start.
        if (off > n / 2) off -= n;
        else if (off < -n / 2) off += n;
        const abs = Math.abs(off);
        const sign = off === 0 ? 0 : off / abs;
        if (abs > 3) {
            return { opacity: 0, pointerEvents: 'none', transform: `translateX(-50%) translateX(${off * 26}%) scale(.4)` };
        }
        return {
            transform: `translateX(-50%) translateX(${off * 46}%) rotateY(${-sign * 44}deg) scale(${Math.max(0.6, 1 - abs * 0.16)})`,
            zIndex: 100 - abs,
            opacity: abs > 2 ? 0.3 : 1 - abs * 0.16,
        };
    };

    return (
        <>
            <div className="wrap">
                <div className="gcf reveal">
                    <div className="gcf__stage">
                        {photos.map((p, i) => (
                            <button
                                type="button"
                                key={i}
                                className={"gcf__slide" + (i === active ? " is-active" : "")}
                                style={slideStyle(i)}
                                onClick={() => (i === active ? lb.open(i) : setActive(i))}
                                aria-label={i === active ? `Open photo ${i + 1}` : `Go to photo ${i + 1}`}
                                tabIndex={Math.abs(i - active) > 3 ? -1 : 0}
                            >
                                <img className="gcf__bg" src={p.src} alt="" aria-hidden="true" loading="lazy" draggable="false" />
                                <img src={p.src} alt={p.cap || ""} loading="lazy" draggable="false" />
                                {i === active && (
                                    <span className="gcf__zoom" aria-hidden="true">
                                        <svg viewBox="0 0 24 24" fill="none">
                                            <circle cx="11" cy="11" r="7" stroke="currentColor" strokeWidth="2" />
                                            <path d="M11 8v6M8 11h6M20 20l-3.5-3.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                                        </svg>
                                    </span>
                                )}
                            </button>
                        ))}
                    </div>

                    {photos[active].cap && <p className="gcf__cap">{photos[active].cap}</p>}

                    <div className="gcf__controls">
                        <button type="button" className="gcf__btn" onClick={() => go(-1)} aria-label="Previous">
                            <svg viewBox="0 0 24 24" fill="none"><path d="M15 6l-6 6 6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg>
                        </button>
                        <div className="gcf__dots">
                            {photos.map((p, i) => (
                                <button
                                    type="button"
                                    key={i}
                                    className={"gcf__dot" + (i === active ? " is-active" : "")}
                                    onClick={() => setActive(i)}
                                    aria-label={`Go to photo ${i + 1}`}
                                />
                            ))}
                        </div>
                        <button type="button" className="gcf__btn" onClick={() => go(1)} aria-label="Next">
                            <svg viewBox="0 0 24 24" fill="none"><path d="M9 6l6 6-6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg>
                        </button>
                    </div>
                </div>
            </div>
            {lb.lightbox}
        </>
    );
};

export default GalleryCoverflowCarousel;
